// components/unit/UnitCard.tsx
/**
 * @mockup /design/mockups/01-mobile-building-landing.png
 * @description Unit listing card with thumbnail, price, Save + View Details buttons
 * Design System: Warm Sophistication - terracotta accent, warm neutrals, Fraunces/Outfit typography
 */
import { useNavigate } from 'react-router-dom';
import { Heart, ChevronRight } from 'lucide-react';
import { formatPrice, formatAvailability, cn } from '../../lib/utils';
import type { Unit } from '../../types/database';
import { PLACEHOLDER_IMAGES } from '../../lib/mockData';
import { useSavedUnits } from '../../context/SavedUnitsContext';
import { useToast } from '../ui/Toast';

interface UnitCardProps {
  unit: Unit;
  onViewDetails?: () => void;
}

export function UnitCard({ unit, onViewDetails }: UnitCardProps) {
  const navigate = useNavigate();
  const { isSaved, toggleSave, count } = useSavedUnits();
  const { showToast } = useToast();
  const availability = formatAvailability(unit.availability_date);
  const isAvailableNow = availability === 'Available Now';
  const saved = isSaved(unit.id);

  const handleSaveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const wasAdded = toggleSave(unit);

    if (wasAdded) {
      if (count === 0) {
        showToast('Saved! Save another to compare side-by-side', 'info');
      } else if (count === 1) {
        showToast('Ready to compare 2 units!', 'success', {
          label: 'Compare Now',
          onClick: () => navigate('/compare'),
        });
      } else {
        showToast(`Added! ${count + 1} units to compare`, 'success');
      }
    } else {
      showToast('Removed from saved', 'info');
    }
  };

  return (
    <div className="bg-surface border border-border rounded-lg overflow-hidden mb-3 transition-all duration-normal hover:border-border-strong">
      {/* Main Content - Clickable for quick navigation */}
      <div
        onClick={onViewDetails}
        className={cn(
          'flex p-4',
          onViewDetails && 'cursor-pointer hover:bg-surface-hover active:bg-bg-subtle transition-colors duration-fast'
        )}
      >
        {/* Thumbnail */}
        <div className="w-24 h-24 bg-bg-subtle rounded-md flex-shrink-0 overflow-hidden">
          <img
            src={PLACEHOLDER_IMAGES.livingRoom}
            alt={`${unit.unit_type?.name || 'Unit'} ${unit.unit_number}`}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Info Stack */}
        <div className="flex-1 ml-4 flex flex-col justify-between py-0.5">
          <div>
            <h3 className="font-display text-display-sm text-text">
              {unit.unit_type?.name || 'Unit'}
            </h3>
            <p className="text-price-md text-primary font-semibold">
              {formatPrice(unit.price)}<span className="text-body-sm font-normal text-text-muted">/mo</span>
            </p>
          </div>

          <div className="text-body-sm text-text-secondary">
            <span>Floor {unit.floor} • Unit {unit.unit_number}</span>
            {unit.square_feet && <span> • {unit.square_feet} sqft</span>}
          </div>

          <div className={cn(
            'text-body-sm font-medium flex items-center gap-1.5',
            isAvailableNow ? 'text-success' : 'text-warning'
          )}>
            <span className={cn(
              'w-2 h-2 rounded-full',
              isAvailableNow ? 'bg-success animate-pulse' : 'bg-warning'
            )} />
            {availability}
          </div>
        </div>
      </div>

      {/* Action Buttons - Big, thumb-friendly */}
      <div className="flex gap-3 px-4 pb-4 pt-1">
        {/* Save Button - 50% width */}
        <button
          onClick={handleSaveClick}
          className={cn(
            'flex-1 flex items-center justify-center gap-2',
            'py-3 px-4 rounded-md font-body font-medium text-body-sm',
            'transition-all duration-fast min-h-[48px]',
            saved
              ? 'bg-primary-subtle text-primary border-[1.5px] border-primary/30'
              : 'bg-bg-subtle text-text-secondary border-[1.5px] border-border hover:border-border-strong hover:text-text active:bg-border-subtle'
          )}
        >
          <Heart
            size={20}
            className={cn(
              'transition-transform',
              saved && 'fill-current'
            )}
          />
          <span>{saved ? 'Saved' : 'Save'}</span>
        </button>

        {/* View Unit Button - 50% width */}
        {onViewDetails && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails();
            }}
            className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-md font-body font-medium text-body-sm bg-primary text-text-inverse hover:bg-primary-hover hover:-translate-y-0.5 active:bg-primary-active active:translate-y-0 transition-all duration-fast min-h-[48px]"
          >
            <span>View Unit</span>
            <ChevronRight size={18} />
          </button>
        )}
      </div>
    </div>
  );
}
